import api from 'api';
import {get} from 'lodash';
import {useState, useCallback, useMemo} from 'react';
import toast from 'react-hot-toast';
import {useInfiniteQuery, useMutation} from 'react-query';
import {ListResponse, WebhookDelivery} from 'types';

const PAGE_SIZE = 10;

const getDeliveries = async (endpointId: string, offset: number) => {
  const {data} = await api.get<ListResponse<WebhookDelivery>>(
    '/integrations/webhook-deliveries/',
    {params: {endpoint: endpointId, limit: PAGE_SIZE, offset}}
  );
  return data;
};

const retryDelivery = async (id: number) => {
  const {data} = await api.post(
    `/integrations/webhook-deliveries/${id}/retry/`
  );
  return data;
};

const DELIVERY_LIST_SCHEMA = [
  {
    dataKey: 'event_type',
    header: 'Event',
    sortable: true,
    index: 0,
  },
  {
    dataKey: 'status',
    header: 'Status',
    sortable: true,
    index: 1,
  },
  {
    dataKey: 'response_status',
    header: 'HTTP Status',
    sortable: true,
    index: 2,
  },
  {
    dataKey: 'attempt_count',
    header: 'Attempts',
    sortable: true,
    index: 3,
  },
  {
    dataKey: 'created_at',
    header: 'Created',
    sortable: true,
    index: 4,
  },
  {
    dataKey: 'retry',
    header: '',
    sortable: false,
    index: 5,
    colWidth: '50px',
  },
];

export const useDeliveryHistory = (webhookId: string) => {
  const [selectedDelivery, setSelectedDelivery] =
    useState<WebhookDelivery | null>(null);

  const {
    data,
    refetch,
    isLoading: isLoadingDeliveries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['webhook-deliveries', webhookId],
    ({pageParam = 0}) => getDeliveries(webhookId, pageParam),
    {
      enabled: !!webhookId,
      getNextPageParam: (lastPage) => {
        const nextOffset = lastPage.offset + lastPage.limit;
        return nextOffset < lastPage.count ? nextOffset : undefined;
      },
    }
  );

  const deliveries = useMemo(
    () => (data ? data?.pages?.flatMap((page) => page.results) : []),
    [data]
  );

  const {mutateAsync: retryDeliveryMutation, isLoading: isRetrying} =
    useMutation({
      mutationFn: retryDelivery,
      onSuccess: () => {
        toast.success('Delivery retry initiated');
        refetch();
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to retry delivery'
        );
        toast.error(message);
      },
    });

  const handleDeliveryClick = useCallback(
    (item: {id?: number | string}) => {
      const delivery = deliveries.find((d) => d.id === item.id);
      if (delivery) {
        setSelectedDelivery(
          selectedDelivery?.id === delivery.id ? null : delivery
        );
      }
    },
    [deliveries, selectedDelivery]
  );

  return {
    deliveries,
    isLoadingDeliveries,
    DELIVERY_LIST_SCHEMA,
    retryDeliveryMutation,
    isRetrying,
    selectedDelivery,
    setSelectedDelivery,
    handleDeliveryClick,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
